package com.essntl.core.unsplash.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.checkbox.EsCheckBox
import com.essntl.uikit.components.checkbox.EsCheckBoxColors
import com.essntl.uikit.components.iconbutton.destructive.EsLargeDestructiveIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.image.AsyncImage
import com.essntl.uikit.components.image.AsyncUrlImage
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme

@Composable
fun UnsplashImage(
    url: String,
    enabled: Boolean = true,
    selected: Boolean = false,
    deletable: Boolean = true,
    selectable: Boolean = true,
    bordered: Boolean = true,
    onDelete: () -> Unit = {},
    onSelect: () -> Unit = {},
    onClick: () -> Unit = {},
    imageSize: DpSize? = null,
    modifier: Modifier = Modifier,
) {
    UnsplashImage(
        model = if (url.startsWith("https://images.unsplash"))
            "${url}?w=500"
        else
            url,
        enabled = enabled,
        selected = selected,
        deletable = deletable,
        selectable = selectable,
        bordered = bordered,
        onDelete = onDelete,
        onSelect = onSelect,
        onClick = onClick,
        imageSize = imageSize,
        modifier = modifier,
    )
}

@Composable
fun UnsplashImage(
    model: Any,
    enabled: Boolean = true,
    selected: Boolean = false,
    deletable: Boolean = true,
    selectable: Boolean = true,
    bordered: Boolean = true,
    onDelete: () -> Unit = {},
    onSelect: () -> Unit = {},
    onClick: () -> Unit = {},
    imageSize: DpSize? = null,
    modifier: Modifier = Modifier,
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()

    Box(
        Modifier
            .then(
                if (bordered)
                    modifier.border(
                        width = 2.dp,
                        color = if (selected)
                            MaterialTheme.esColorScheme.borderAccent else
                            MaterialTheme.esColorScheme.borderSecondary,
                        shape = RoundedCornerShape(20.dp),
                    )
                else modifier,
            )
            .then(
                if (imageSize != null)
                    modifier
                        .width(imageSize.width)
                        .height(imageSize.height)
                else modifier.fillMaxSize(),
            ).clip(RoundedCornerShape(20.dp))
            .clickable(
                enabled = enabled,
                interactionSource = interactionSource,
                indication = null,
                onClick = { onClick() },
            ),
    ) {
        AsyncImage(
            data = model,
            placeholder = {
                Box(
                    contentAlignment = Alignment.Center,
                    modifier = Modifier
                        .fillMaxSize()
                        .background(
                            color = MaterialTheme.esColorScheme.bgPrimary,
                            shape = RoundedCornerShape(20.dp),
                        ),
                ) {
                    EsSpinner(
                        color = MaterialTheme.esColorScheme.iconPrimary,
                        size = EsSpinnerSize.Large,
                        modifier = Modifier
                            .align(Alignment.Center),
                    )
                }
            },
            contentDescription = null,
            contentScale = ContentScale.Crop,
            modifier = Modifier.fillMaxSize(),
        )

        if (isHovered && enabled) {
            if (selectable) {
                EsCheckBox(
                    colors = EsCheckBoxColors(
                        backgroundCheckedBoxColor = MaterialTheme.esColorScheme.secondaryHover,
                        backgroundUncheckedBoxColor = MaterialTheme.esColorScheme.secondaryHover,
                        borderCheckedBoxColor = MaterialTheme.esColorScheme.borderAccent,
                        checkMarkColor = MaterialTheme.esColorScheme.iconAccent,
                        disabledCheckedBoxColor = MaterialTheme.esColorScheme.secondaryDisabled,
                        disabledCheckMarkColor = MaterialTheme.esColorScheme.iconDisabled,
                    ),
                    checked = selected,
                    onCheckedChange = {
                        onSelect()
                    },
                    modifier = Modifier
                        .align(Alignment.TopStart)
                        .padding(top = 2.5.spacing, start = 1.spacing),
                )
            }

            if (deletable)
                EsLargeDestructiveIconButton(
                    onClick = onDelete,
                    iconRes = EsIcons.Trash,
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(
                            top = 1.spacing,
                            end = 1.spacing,
                        ),
                )
        }
    }
}